import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Injector, NgZone, runInInjectionContext } from '@angular/core';
import { Messaging, getToken, onMessage, getMessaging as _getMessaging } from '@angular/fire/messaging';
import { environment } from '../../../environments/environment';
import { Platform } from '@angular/cdk/platform';
import { Subject, BehaviorSubject, Observable, timer, EMPTY } from 'rxjs';
import { initializeApp } from '@angular/fire/app';
import { switchMap, retry, share } from 'rxjs/operators';
import { WebSocketService } from '../websocket/websocket.service';

export interface NotificationResponse {
  status: boolean;
  code: number;
  result: Notification[];
  lastPage: number;
  loadmore: string;
  err: any;
}

export interface Notification {
  id: number;
  user_id: number;
  type: string;
  action_id: number;
  action_by_user_id: number;
  is_notified: string;
  is_read: string;
  created_at: string;
  updated_at: string;
  profile_img: string;
  from_username: string;
  to_username: string;
  event_name: string;
  event_slug: string;
  message: string;
  /** Optional slug for redirect (e.g. "training/bookings" for training notifications). */
  slug?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private deviceToken: string | null = null;
  private isSupported: boolean = false;
  private messagingInstance: Messaging | null = null;
  private readonly POLLING_INTERVAL_MS = 30000;

  private readonly _notificationReceived$ = new Subject<any>();
  private readonly _isInitialized$ = new BehaviorSubject<boolean>(false);
  private readonly _notifications$ = new BehaviorSubject<Notification[]>([]);
  private readonly _unreadCount$ = new BehaviorSubject<number>(0);
  private readonly _lastPage$ = new BehaviorSubject<number>(1);
  private readonly _currentPage$ = new BehaviorSubject<number>(1);
  private readonly _loadingMore$ = new BehaviorSubject<boolean>(false);

  /** Public observables – consumers subscribe only; state is updated internally */
  readonly notificationReceived$ = this._notificationReceived$.asObservable();
  readonly isInitialized$ = this._isInitialized$.asObservable();
  readonly notifications$ = this._notifications$.asObservable();
  readonly unreadCount$ = this._unreadCount$.asObservable();
  readonly lastPage$ = this._lastPage$.asObservable();
  readonly currentPage$ = this._currentPage$.asObservable();
  readonly isLoadingMore$ = this._loadingMore$.asObservable();

  private pollingSubscription: any;
  private notificationSubscription: any = null;
  private isWebSocketSubscribed = false;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private injector: Injector,
    private platform: Platform,
    private websocketService: WebSocketService
  ) {
    this.checkSupport();
    this.setupServiceWorkerMessageListener();
    if (this.isBrowser() && this.isAuthenticated()) {
      this.subscribeToNotifications();
      this.loadInitialNotifications();
    }
  }

  /** Safe for SSR: avoid localStorage/window when not in browser */
  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  }

  private logError(context: string, err: unknown): void {
    if (!environment.production) {
      console.warn(`[NotificationService] ${context}`, err);
    }
  }

  private checkSupport(): void {
    this.isSupported =
      this.isBrowser() &&
      !this.platform.IOS &&
      !this.platform.SAFARI &&
      typeof (window as any).Notification !== 'undefined';
  }

  // Helper to get Messaging only if supported and provided
  private getMessaging(): Messaging | null {
    if (!this.isSupported) {
      return null;
    }

    if (this.messagingInstance) {
      return this.messagingInstance;
    }

    try {
      return runInInjectionContext(this.injector, () => {
        if (!this.messagingInstance) {
          const app = initializeApp(environment.firebaseConfig);
          this.messagingInstance = _getMessaging(app);
        }
        return this.messagingInstance;
      });
    } catch (error) {
      console.error('Error getting messaging instance:', error);
      return null;
    }
  }

  private isAuthenticated(): boolean {
    if (!this.isBrowser()) return false;
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user_id');
    return !!(token && userId);
  }

  private startPollingIfAuthenticated() {
    // This method is kept for backward compatibility but polling is replaced with WebSocket
    if (this.isAuthenticated()) {
      // Polling disabled - using WebSocket instead
    }
  }

  /**
   * Public method to start WebSocket subscription after authentication
   * Should be called after successful login
   */
  startPollingAfterAuth() {
    if (!this.isWebSocketSubscribed) {
      this.subscribeToNotifications();
      this.loadInitialNotifications();
    }
  }

  private startPolling(): void {
    this.pollingSubscription = timer(0, this.POLLING_INTERVAL_MS).pipe(
      switchMap(() => {
        // Check authentication before each API call
        if (!this.isAuthenticated()) {
          // Stop polling if user is not authenticated
          this.stopPolling();
          return EMPTY;
        }
        return this.getNotifications();
      }),
      retry({
        delay: 5000 // Retry after 5 seconds if there's an error
      }),
      share()
    ).subscribe({
      next: (response: any) => {
        if (response && response.result) {
          this._notifications$.next(response.result);
          const unreadCount = response.result.filter((n: Notification) => n.is_read === 'n').length;
          this._unreadCount$.next(unreadCount);
        }
      },
      error: (err) => this.logError('polling', err)
    });
  }

  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  private subscribeToNotifications(): void {
    if (!this.isAuthenticated() || this.isWebSocketSubscribed) {
      return;
    }

    const userId = localStorage.getItem('user_id');
    if (!userId) return;

    try {
      this.websocketService.subscribeToNotifications(userId);
      this.isWebSocketSubscribed = true;

      this.notificationSubscription = this.websocketService.onNotification().subscribe({
        next: (notification: any) => {
          this.zone.run(() => this.handleNewNotification(notification));
        },
        error: (err) => this.logError('WebSocket notification stream', err)
      });
    } catch (err) {
      this.logError('subscribeToNotifications', err);
    }
  }

  private handleNewNotification(notification: any): void {
    const data = notification?.notification ?? notification;
    if (!data || (data.id == null && data.id !== 0)) {
      this.logError('handleNewNotification: invalid payload (missing id)', { notification: !!notification });
      return;
    }

    const newNotification: Notification = {
      id: data.id,
      user_id: data.user_id,
      type: data.type,
      action_id: data.action_id,
      action_by_user_id: data.action_by_user_id,
      is_notified: data.is_notified || 'n',
      is_read: data.is_read || 'n',
      created_at: data.created_at,
      updated_at: data.updated_at,
      profile_img: data.profile_img || '',
      from_username: data.from_username || '',
      to_username: data.to_username || '',
      event_name: data.event_name || '',
      event_slug: data.event_slug || '',
      message: data.message || '',
      slug: data.slug,
    };

    const currentNotifications = this._notifications$.value;
    if (currentNotifications.some(n => n.id === newNotification.id)) return;

    this._notifications$.next([newNotification, ...currentNotifications]);
    if (newNotification.is_read === 'n') {
      this._unreadCount$.next(this._unreadCount$.value + 1);
    }

    if (this.isBrowser() && typeof (window as any).Notification !== 'undefined' && (window as any).Notification.permission === 'granted' && document.hidden) {
      this.showBrowserNotification(newNotification);
    }
  }

  private showBrowserNotification(notification: Notification): void {
    if (!this.isBrowser() || typeof (window as any).Notification === 'undefined') return;
    try {
      new (window as any).Notification(notification.message || 'New Notification', {
        icon: notification.profile_img || '/assets/icons/icon-72x72.png',
        tag: `notification-${notification.id}`,
      });
    } catch (err) {
      this.logError('showBrowserNotification', err);
    }
  }

  private loadInitialNotifications(): void {
    this.getNotifications(1).subscribe({
      next: (response: NotificationResponse) => {
        if (response && response.result) {
          this._notifications$.next(response.result);
          const unreadCount = response.result.filter((n: Notification) => n.is_read === 'n').length;
          this._unreadCount$.next(unreadCount);
        }
        if (response && response.lastPage != null) {
          this._lastPage$.next(response.lastPage);
        }
        this._currentPage$.next(1);
      },
      error: (err) => this.logError('loadInitialNotifications', err)
    });
  }

  /**
   * Load next page and append to notifications. Single source of truth for list + pagination.
   */
  loadMoreNotifications(): void {
    if (this._loadingMore$.value) return;
    const current = this._currentPage$.value;
    const last = this._lastPage$.value;
    if (current >= last) return;

    this._loadingMore$.next(true);
    const nextPage = current + 1;

    this.getNotifications(nextPage).subscribe({
      next: (response: NotificationResponse) => {
        if (response?.status && response?.result?.length) {
          const list = [...this._notifications$.value, ...response.result];
          this._notifications$.next(list);
          const unreadCount = list.filter((n: Notification) => n.is_read === 'n').length;
          this._unreadCount$.next(unreadCount);
        }
        if (response?.lastPage != null) {
          this._lastPage$.next(response.lastPage);
        }
        this._currentPage$.next(nextPage);
        this._loadingMore$.next(false);
      },
      error: (err) => {
        this.logError('loadMoreNotifications', err);
        this._loadingMore$.next(false);
      }
    });
  }

  /**
   * Reload page 1 (e.g. after new push notification). Replaces list and resets pagination.
   */
  refreshNotifications(): void {
    this.getNotifications(1).subscribe({
      next: (response: NotificationResponse) => {
        if (response?.result) {
          this._notifications$.next(response.result);
          const unreadCount = response.result.filter((n: Notification) => n.is_read === 'n').length;
          this._unreadCount$.next(unreadCount);
        }
        if (response?.lastPage != null) {
          this._lastPage$.next(response.lastPage);
        }
        this._currentPage$.next(1);
      },
      error: (err) => this.logError('refreshNotifications', err)
    });
  }

  private unsubscribeFromNotifications(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = null;
    }
    this.websocketService.unsubscribeFromNotifications();
    this.isWebSocketSubscribed = false;
  }

  /**
   * Call on logout so the next login can subscribe again and state is clean.
   * Safe to call even when not subscribed.
   */
  teardownForLogout(): void {
    this.unsubscribeFromNotifications();
    this._notifications$.next([]);
    this._unreadCount$.next(0);
    this._lastPage$.next(1);
    this._currentPage$.next(1);
  }

  getNotifications(page: number = 1): Observable<NotificationResponse> {
    if (!this.isBrowser() || !this.isAuthenticated()) {
      return new Observable<NotificationResponse>(observer => observer.complete());
    }

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      userid: localStorage.getItem('user_id'),
      page: page,
      limit: 8
    };

    return this.http.post<NotificationResponse>(
      `${environment.apiUrl}/getAllNotification`,
      payload,
      { headers }
    );
  }

  async markAllNotificationsAsRead() {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      user_id : localStorage.getItem('user_id')
    };

    try {
      await this.http.post(`${environment.apiUrl}/userReadAllNotification`, payload, { headers })
        .toPromise();

      const currentNotifications = this._notifications$.value;
      this._notifications$.next(currentNotifications.map(n => ({ ...n, is_read: 'y' })));
      this._unreadCount$.next(0);
    } catch (error) {
      this.logError('markAllNotificationsAsRead', error);
      throw error;
    }
  }

  async markNotificationAsRead(notificationId: number) {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    const payload = {
      user_id: localStorage.getItem('user_id'),
      notification_id: notificationId
    };

    try {
      await this.http.post(`${environment.apiUrl}/userReadNotification`, payload, { headers })
        .toPromise();

      const currentNotifications = this._notifications$.value;
      const updatedNotifications = currentNotifications.map(n =>
        n.id === notificationId ? { ...n, is_read: 'y' } : n
      );
      this._notifications$.next(updatedNotifications);
      this._unreadCount$.next(updatedNotifications.filter(n => n.is_read === 'n').length);
    } catch (error) {
      this.logError('markNotificationAsRead', error);
      throw error;
    }
  }

  notificationSettings(
    chat_notification: string,
    email_notification: string,
    event_notification: string
  ) {
    const payload = {
      user_id: localStorage.getItem('user_id'),
      chat_notification,
      email_notification,
      event_notification
    };

    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    return this.http.post(environment.apiUrl + '/updateNotificationSettings', payload, {
      headers
    });
  }

  /** Call from a user interaction (e.g. button click) to request push permission */
  async initializeNotifications(): Promise<void> {
    if (!this.isBrowser() || !this.isSupported) return;

    try {
      const permission = await (window as any).Notification.requestPermission();

      if (permission === 'granted') {
        const token = await this.fetchDeviceToken();
        if (token) {
          await this.setupMessageListener();
        }
      } else {
        console.warn('Notification permission not granted');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
    }
  }

  private setupServiceWorkerMessageListener(): void {
    if (!this.isBrowser() || !('serviceWorker' in (window as any).navigator)) return;
    const nav = (window as any).navigator;
    if (!nav.serviceWorker) return;
    nav.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data.type === 'PUSH_NOTIFICATION') {
          this.zone.run(() => {
            this.handleNotificationPayload(event.data.payload);
          });
        } else if (event.data.type === 'NOTIFICATION_CLICKED') {
          this.zone.run(() => {
            this.handleNotificationClick(event.data.payload);
          });
        }
    });
  }

  private handleNotificationClick(payload: any) {
    // Handle the click based on the payload data
    const clickAction = payload?.click_action || 'https://theselfwish.com';

    // You can implement custom navigation logic here
    if (clickAction.startsWith('https://theselfwish.com')) {
      // Internal navigation - extract the path
      const path = new URL(clickAction).pathname;
      // You can use Router.navigate here if needed
      window.location.href = clickAction;
    } else {
      // External link
      window.open(clickAction, '_blank');
    }
  }

  private handleNotificationPayload(payload: any) {
    try {
      const notificationData = {
        title: payload.notification?.title || (payload.data && payload.data['title']) || 'New Notification',
        body: payload.notification?.body || (payload.data && payload.data['body']) || '',
        timestamp: new Date(),
        data: payload.data || {},
        link: payload.fcmOptions?.link || (payload.data && payload.data['click_action']) || 'https://theselfwish.com'
      };

      this._notificationReceived$.next(notificationData);

      if (this.isBrowser() && typeof (window as any).Notification !== 'undefined' && document.hidden && (window as any).Notification.permission === 'granted') {
        const notification = new (window as any).Notification(notificationData.title, {
          body: notificationData.body,
          icon: '/assets/icons/icon-72x72.png',
          tag: 'notification-1',
          data: {
            ...notificationData.data,
            click_action: notificationData.link
          }
        });

        // Handle notification click in foreground
        notification.onclick = () => {
          this.handleNotificationClick({
            click_action: notificationData.link,
            ...notificationData.data
          });
        };
      }
    } catch (err) {
      this.logError('handleNotificationPayload', err);
    }
  }

  private async setupMessageListener() {
    const messaging = this.getMessaging();
    if (!messaging) {
      console.error('No messaging instance available');
      return;
    }

    try {
      runInInjectionContext(this.injector, () => {
        onMessage(messaging, (payload) => {
          this.zone.run(() => {
            this.handleNotificationPayload(payload);
          });
        });
      });

      this._isInitialized$.next(true);
    } catch (err) {
      this.logError('setupMessageListener', err);
      this._isInitialized$.next(false);
    }
  }

  async fetchDeviceToken(): Promise<string | null> {
    if (!this.isSupported) {
      return null;
    }

    const messaging = this.getMessaging();
    if (!messaging) {
      console.error('No messaging instance available');
      return null;
    }

    try {
      const token = await runInInjectionContext(this.injector, () =>
        getToken(messaging, {
          vapidKey: environment.firebaseConfig.vapidKey
        })
      );

      if (token) {
        this.deviceToken = token;
        return token;
      } else {
        console.warn('No token generated');
        return null;
      }
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.unsubscribeFromNotifications();
  }
}
